import React, { useEffect, useMemo, useState } from 'react';
import { ProgressStepBar } from '../CreditApplicationComponents/ProgressStepBar';
import { PreviewCreditApplicationData } from '../CreditApplicationComponents/PreviewCreditApplicationData';
import DynamicStepForm from '../CreditApplicationComponents/DynamicStepForm';
import StepFormButtons from '../CreditApplicationComponents/StepFormButtons';
import {
  ConditionalOptionValues,
  DynamicInvitedSignaturesFields,
} from '@dill/dill-shared/dist/src';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';

type Field = {
  name: string;
  id: string;
  fromField:
    | 'TEXT'
    | 'NUMBER'
    | 'CURRENCY'
    | 'DATE'
    | 'TEXT_AREA'
    | 'CHECK_BOX'
    | 'FILE'
    | 'OPTION_FIELD'
    | 'DOCUMENT_FIELD'
    | 'INVITE_USER'
    | 'CONDITIONAL_OPTION'
    | 'BANK_REFERENCE'
    | 'TRADE_REFERENCE'
    | 'CONDITIONAL_SELECTED_FIELD'
    | 'STATE_DROPDOWN'
    | 'CHECKBOX_LIST'
    | 'CONDITIONAL_FORM'
    | 'DYNAMIC_FORM_DOCUMENT'
    | 'AUTOFILL_CHECKBOX'
    | 'INTERNAL_SIGNER'
    | 'DROPDOWN';
  isRequired: boolean;
  prefix: string;
  suffix: string;
  alignment?: 'LEFT' | 'RIGHT' | 'CENTER';
  documentText?: string;
  isHashed?: boolean;
  isFieldEmail?: boolean;
  isRow?: boolean;
  optionList?: string;
  optionValueSource?: 'NAME' | 'ERP_ID';
  buttonType?: 'PRIMARY' | 'SECONDARY' | 'TERTIARY';
  fontSize?: string;
  fontWeight?: string;
  spaceHeight?: string;
  whoNeedsToSign?: string;
  fullNameNeeded?: boolean;
  driverLicenseNeeded?: boolean;
  optionType?: string;
  secondaryText?: string;
  status?: 'Pending' | 'Requested' | 'Submitted' | 'Rejected';
  emailDate?: string;
  sendEmail?: boolean;
  signatureGroupName?: string;
  placeHolder?: string;
  type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
  minimumCreditAmount?: number;
  invitedSignaturesFields?: DynamicInvitedSignaturesFields[];
  minimumNumberOfSignatures?: number;
  onlyShowPlaidOption?: boolean;
  formTemplate?: string | null;
  showIfNotPlaid?: boolean;
  conditionalVisibility?: {
    sourceFieldId: string;
    sourceOptionValues: string[];
  } | null;
  borderId?: string | null;
  labelSubtext?: string | null;
};

type FieldDetails = {
  id: string;
  index: number;
  columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
  stepType: 'GROUPED' | 'NOT_GROUPED';
  fields: Field[];
  groupedPosition: number;
};

const PreviewCreditApplicationTemplate = ({
  stepsDetails,
}: {
  stepsDetails: {
    name: string;
    id: string;
    position: number;
    fields: {
      columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      stepType: 'GROUPED' | 'NOT_GROUPED';
      groupedFields: FieldDetails[];
      unGroupedFields: Field[];
      groupName?: string;
      position: number;
      isFieldArray: boolean;
      minimunNumberOfGroups?: number;
    }[];
  }[];
}) => {
  const [steps, setSteps] = useState<string[]>([]);
  const [step, setStep] = useState(0);
  const [allStepFieldsValues, setAllStepFieldsValues] = useState<
    {
      uniqueId: string;
      id: string;
      name: string;
      fromField:
        | 'TEXT'
        | 'TEXT_AREA'
        | 'NUMBER'
        | 'CURRENCY'
        | 'DATE'
        | 'CHECK_BOX'
        | 'OPTION_FIELD'
        | 'FILE'
        | 'DOCUMENT_FIELD'
        | 'INVITE_USER'
        | 'DATE'
        | 'CONDITIONAL_OPTION'
        | 'BANK_REFERENCE'
        | 'TRADE_REFERENCE'
        | 'CONDITIONAL_SELECTED_FIELD'
        | 'STATE_DROPDOWN'
        | 'CHECKBOX_LIST'
        | 'CONDITIONAL_FORM'
        | 'DYNAMIC_FORM_DOCUMENT'
        | 'AUTOFILL_CHECKBOX'
        | 'INTERNAL_SIGNER'
        | 'DROPDOWN';
      isRequired: boolean;
      isFieldEmail: boolean;
      isHashed: boolean;
      stepName: string;
      stepNumber: number;
      index: number;
      groupStepPosition: number;
      groupName: string;
      groupColumnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      isFieldArray: boolean;
      groupedPosition: number;
      value: string | number | boolean | File | any | null;
      optionType?: string;
      secondaryText?: string;
      status?:
        | 'Pending'
        | 'Requested'
        | 'Submitted'
        | 'Rejected'
        | 'Blocked'
        | 'Dropped'
        | 'Delivered'
        | 'Deferred'
        | 'Bounce';
      emailDate?: string;
      sendEmail?: boolean;
      signatureGroupName?: string;
      placeHolder?: string;
      type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      onlyShowPlaidOption?: boolean;
      formTemplate?: string | null;
      showIfNotPlaid?: boolean;
      fieldValue?: ConditionalOptionValues[];
      conditionalVisibility?: {
        sourceFieldId: string;
        sourceOptionValues: string[];
      } | null;
    }[]
  >([]);
  const [invitedSignatures, setInvitedSignatures] = useState<
    {
      title: string;
      email: string;
      step: number;
      fields: DynamicInvitedSignaturesFields[];
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    }[]
  >([]);
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const nextStep = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 0));

  const extractInviteUsers = (
    indx: number,
    whoNeedsToSign?: string,
    name?: string,
    type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR',
    minimumCreditAmount?: number,
    fields?: DynamicInvitedSignaturesFields[],
    minimumNumberOfSignatures?: number,
    isRequired?: boolean,
    id?: string
  ): {
    title: string;
    email: string;
    step: number;
    signatureGroupName?: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    fields: DynamicInvitedSignaturesFields[];
    minimumNumberOfSignatures?: number;
    isRequired?: boolean;
    id?: string;
  }[] => {
    if (!whoNeedsToSign) return [];
    return whoNeedsToSign.split(',').map((option) => ({
      title: '',
      email: '',
      step: indx,
      signatureGroupName: name,
      placeHolder: option.trim(),
      type: type ?? 'AUTHORIZED_AGENT',
      minimumCreditAmount,
      fields: fields || [],
      minimumNumberOfSignatures,
      isRequired,
      id,
    }));
  };

  useEffect(() => {
    if (stepsDetails && stepsDetails.length > 0) {
      const templateSteps = stepsDetails.map((st) => st.name);
      templateSteps.push('Review & Submit');
      setSteps(templateSteps);

      const allStepFields = stepsDetails.flatMap((st) => {
        return st.fields.flatMap((field) => {
          if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
            return field.unGroupedFields.map((f) => {
              const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                field.columnType
              } ${0} ${f.id || ''}`
                .toLowerCase()
                .replace(/\s+/g, '-');
              let value =
                f.fromField === 'TEXT' ||
                f.fromField === 'TEXT_AREA' ||
                f.fromField === 'OPTION_FIELD'
                  ? ''
                  : f.fromField === 'NUMBER' || f.fromField === 'CURRENCY'
                  ? 0
                  : f.fromField === 'CHECK_BOX'
                  ? false
                  : null;
              const foundField = allStepFieldsValues.find((stFd) => stFd.uniqueId === uniqueId);
              if (foundField) {
                value = foundField.value;
              }
              if (f.fromField === 'DOCUMENT_FIELD') {
                value = f.documentText || '';
              }
              return {
                uniqueId: uniqueId,
                id: f.id || '',
                name: f.name || '',
                fromField: f.fromField || '',
                isRequired: f.isRequired || false,
                isHashed: f.isHashed || false,
                stepName: st.name,
                stepNumber: st.position || 0,
                groupStepPosition: field.position || 0,
                groupedPosition: 0,
                groupName: field?.groupName || '',
                groupColumnType: field.columnType || ('ONE_COLUMN' as const),
                isFieldArray: false,
                isFieldEmail: f.isFieldEmail || false,
                index: 0,
                value: value,
                ...(f.optionType ? { optionType: f.optionType } : {}),
                ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                ...(f.status ? { status: f.status } : {}),
                ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                ...(f.sendEmail ? { sendEmail: f.sendEmail } : {}),
                ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
                ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
                ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
                ...(f.conditionalVisibility
                  ? { conditionalVisibility: f.conditionalVisibility }
                  : {}),
                ...(foundField?.fieldValue ? { fieldValue: foundField.fieldValue } : {}),
                ...(f.borderId ? { borderId: f.borderId } : {}),
              };
            });
          }

          if (field.stepType === 'GROUPED' && field.groupedFields) {
            return field.groupedFields.flatMap((group) =>
              group.fields.map((f) => {
                const uniqueId = `${st.name} ${field?.groupName} ${field.position} ${
                  field.columnType
                } ${group.groupedPosition} ${f.id || ''}`
                  .toLowerCase()
                  .replace(/\s+/g, '-');
                let value =
                  f.fromField === 'TEXT' ||
                  f.fromField === 'TEXT_AREA' ||
                  f.fromField === 'OPTION_FIELD'
                    ? ''
                    : f.fromField === 'NUMBER' || f.fromField === 'CURRENCY'
                    ? 0
                    : f.fromField === 'CHECK_BOX'
                    ? false
                    : null;
                const foundField = allStepFieldsValues.find((stFd) => stFd.uniqueId === uniqueId);
                if (foundField) {
                  value = foundField.value;
                }
                if (f.fromField === 'DOCUMENT_FIELD') {
                  value = f.documentText || '';
                }
                return {
                  uniqueId: uniqueId,
                  id: f.id || '',
                  name: f.name || '',
                  fromField: f.fromField || '',
                  isRequired: f.isRequired || false,
                  isHashed: f.isHashed || false,
                  stepName: st.name,
                  stepNumber: st.position || 0,
                  groupStepPosition: field.position || 0,
                  groupedPosition: group.groupedPosition || 0,
                  groupName: field?.groupName || '',
                  groupColumnType: group.columnType || ('ONE_COLUMN' as const),
                  isFieldArray: field.isFieldArray || false,
                  isFieldEmail: f.isFieldEmail || false,
                  index: group.index || 0,
                  value: value,
                  ...(f.optionType ? { optionType: f.optionType } : {}),
                  ...(f.secondaryText ? { secondaryText: f.secondaryText } : {}),
                  ...(f.status ? { status: f.status } : {}),
                  ...(f.emailDate ? { emailDate: f.emailDate } : {}),
                  ...(f.sendEmail ? { sendEmail: f.sendEmail } : {}),
                  ...(f.onlyShowPlaidOption ? { onlyShowPlaidOption: f.onlyShowPlaidOption } : {}),
                  ...(f.formTemplate ? { formTemplate: f.formTemplate } : {}),
                  ...(f.showIfNotPlaid ? { showIfNotPlaid: f.showIfNotPlaid } : {}),
                  ...(f.conditionalVisibility
                    ? { conditionalVisibility: f.conditionalVisibility }
                    : {}),
                  ...(foundField?.fieldValue ? { fieldValue: foundField.fieldValue } : {}),
                  ...(f.borderId ? { borderId: f.borderId } : {}),
                };
              })
            );
          }

          return [];
        });
      });
      setAllStepFieldsValues(allStepFields);

      const inviteData: {
        title: string;
        email: string;
        step: number;
        fields: DynamicInvitedSignaturesFields[];
        type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      }[] = [];

      stepsDetails.forEach((stp, index) => {
        stp.fields?.forEach((field) => {
          if (field.stepType === 'NOT_GROUPED' && field.unGroupedFields) {
            field.unGroupedFields.forEach((f) => {
              if (f.fromField === 'INVITE_USER') {
                inviteData.push(
                  ...extractInviteUsers(
                    index,
                    f.whoNeedsToSign,
                    f.name,
                    f.type,
                    f.minimumCreditAmount,
                    f.invitedSignaturesFields || [],
                    f.minimumNumberOfSignatures,
                    f.isRequired,
                    f.id
                  )
                );
              }
            });
          }

          if (field.stepType === 'GROUPED' && field.groupedFields) {
            field.groupedFields.forEach((group) => {
              group.fields.forEach((f) => {
                if (f.fromField === 'INVITE_USER') {
                  inviteData.push(
                    ...extractInviteUsers(
                      index,
                      f.whoNeedsToSign,
                      f.name,
                      f.type,
                      f.minimumCreditAmount,
                      f.invitedSignaturesFields || [],
                      f.minimumNumberOfSignatures,
                      f.isRequired,
                      f.id
                    )
                  );
                }
              });
            });
          }
        });
      });

      setInvitedSignatures(inviteData);
    }
  }, [stepsDetails]);

  const onChangeFormData = ({
    id,
    value,
    fieldValue,
  }: {
    id: string;
    value: any;
    fieldValue?: ConditionalOptionValues[];
  }) => {
    const uniqueId = allStepFieldsValues.find((f) => f.uniqueId === id)?.uniqueId;
    if (uniqueId) {
      setAllStepFieldsValues((prev) =>
        prev.map((f) =>
          f.uniqueId === id
            ? { ...f, value, ...(fieldValue !== undefined ? { fieldValue } : {}) }
            : f
        )
      );
    }
  };

  const domainDetails = useMemo(() => {
    if (user && currentSupplier) {
      const branches = supplierBranches
        .filter((branch) => branch?.notInCreditApp !== true)
        .map((b) => ({ name: b?.branchName ?? '', erpId: b?.erpId ?? '' }));
      const seenSalesReps = new Set<string>();
      const salesReps = supplierBranches
        .filter((branch) => branch?.notInCreditApp !== true)
        .flatMap((branch) => branch?.salesRep ?? [])
        .filter((rep) => {
          const name = rep?.name ?? '';
          if (!name || seenSalesReps.has(name)) return false;
          seenSalesReps.add(name);
          return true;
        })
        .map((rep) => ({ name: rep?.name ?? '', erpId: rep?.erpId ?? '' }));
      return {
        id: currentSupplier.id,
        domainId: currentSupplier.domainId ?? '',
        url: currentSupplier?.logo?.url ?? '',
        name: currentSupplier.name ?? '',
        branches,
        salesReps,
      };
    }
    return null;
  }, [user, currentSupplier, supplierBranches]);

  return (
    <div className="w-full p-4 rounded-lg">
      <p className="font-bold text-xl mb-1">Preview</p>
      {stepsDetails.length === 0 && (
        <div className=" h-[calc(100vh-200px)] overflow-y-auto">
          <div className="w-full WHITE-BG p-4">
            <p className="text-gray-500">No steps available for preview.</p>
          </div>
        </div>
      )}
      {stepsDetails.length > 0 && (
        <div className=" h-[calc(100vh-200px)] overflow-y-auto">
          <div className="py-3">
            <ProgressStepBar currentStep={step} steps={steps} />
          </div>
          <div className="w-full WHITE-BG p-4">
            {step === steps.length - 1 ? (
              <div>
                <h2 className="text-xl font-semibold mb-4">Review & Submit</h2>
                <PreviewCreditApplicationData formData={allStepFieldsValues} />
              </div>
            ) : (
              <DynamicStepForm
                title={stepsDetails[step]?.name}
                stepFields={stepsDetails[step]?.fields || []}
                formData={allStepFieldsValues}
                onChange={onChangeFormData}
                enableAutofillCheckboxes
                errors={[]}
                onAddGroupedField={() => {}}
                onRemoveGroupedField={() => {}}
                onUpdateInviteSigners={() => {}}
                invitedSignatures={invitedSignatures}
                invitedSignaturesErrors={[]}
                bankReference={null}
                bankError={null}
                onUpdateBankReference={() => {}}
                domainDetails={domainDetails}
                step={step}
              />
            )}
            <StepFormButtons
              step={step}
              minStep={0}
              maxStep={steps.length - 1}
              onPrevious={prevStep}
              onContinue={nextStep}
              isLoading={false}
              isDisabled={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewCreditApplicationTemplate;
